import { useTranslation } from 'react-i18next';

import { stepNavTitle, type WorkflowStep } from '@/api/workflows';

/**
 * "Step 4 of 12 — Document Upload", with a segment per step underneath.
 *
 * ## Why it only exists on a narrow shell
 *
 * On a wide shell the rail IS this: a 280px column, permanently on screen,
 * naming every step and marking the one you are on. Below 900px that column
 * becomes a slide-in drawer (see `.form-shell__rail` in theme/global.css), and
 * the advisor loses the only thing on the page that says where they are in the
 * flow — they have to open the drawer to find out, which is a navigation
 * gesture asked of someone who only wanted to orient themselves.
 *
 * So this is the drawer's stand-in, not a second rail: it answers "where am I"
 * and nothing else. It is not clickable, because the drawer one tap away is
 * already the navigator, and two competing step controls at the top of a phone
 * screen is worse than one.
 *
 * Hidden by CSS above 900px rather than by a prop, for the same reason every
 * other piece of this shell's reflow is: `.form-shell` is a query container, so
 * the rule fires on the SHELL's width wherever it is mounted — a device-preview
 * frame or a genuinely narrow screen — with no `window.innerWidth` read to go
 * stale on rotation.
 *
 * ## What it counts
 *
 * Exactly the steps the rail lists — already filtered by the caller to the ones
 * this runtime will walk (audience-eligible, conditions met). A step the flow
 * skips must not be counted, or "Step 4 of 12" on a form that only ever shows
 * nine steps would be a promise the advisor never gets to collect on. The flip
 * side is that the total MOVES as answers open and close steps, which is
 * correct and is what the rail already does.
 *
 * A step that is not in that list — ThankYou in advisor runtime, say — renders
 * nothing at all rather than a bare "Step 0 of 12".
 */
export function StepProgress({
  steps,
  activeStepId,
  language,
}: {
  /** The rail's own step list: audience-eligible, conditions met, in walk order. */
  steps: WorkflowStep[];
  activeStepId: string;
  language: 'en' | 'fr';
}) {
  const { t } = useTranslation();
  const index = steps.findIndex((s) => s.id === activeStepId);
  const active = index < 0 ? undefined : steps[index];
  if (!active) return null;

  // Named the way the rail names it — the drawer this stands in for is where the
  // advisor will go looking for the same step, so the two have to agree.
  const title = stepNavTitle(active, language);

  return (
    <div className="form-shell__step-progress">
      <p className="form-shell__step-progress-label">
        <strong>
          {t('formRenderer.stepProgress.counter', {
            current: index + 1,
            total: steps.length,
          })}
        </strong>
        {` - ${title}`}
      </p>
      {/* Decorative: the line above already states the position in words, and a
          screen reader reciting twelve segments would be noise, not information. */}
      <div className="form-shell__step-progress-track" aria-hidden>
        {steps.map((step, i) => (
          <span
            key={step.id}
            className={`form-shell__step-progress-seg${
              i <= index ? ' form-shell__step-progress-seg--done' : ''
            }`}
          />
        ))}
      </div>
    </div>
  );
}
